import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import Pagination from '@/components/ui/Pagination'
import TabBar, { type TabBarOption } from '@/components/ui/TabBar'
import TitleRow from '@/components/ui/TitleRow'
import { useDepthSectors } from '@/features/depth/api/queries'
import { useMovers, useMoversBrokers } from '@/features/movers/api/queries'
import MoversBrokersTable from '@/features/movers/components/MoversBrokersTable'
import MoversSectorFilter from '@/features/movers/components/MoversSectorFilter'
import MoversTable from '@/features/movers/components/MoversTable'
import { MoversBoardSkeleton } from '@/screens/market/components/MoversBoardSkeleton'
import {
  loadMovers,
  moversSearchSchema,
  toBrokersQuery,
  toMoversQuery,
  toTableQuery,
  type MoversScreenView,
} from '@/screens/market/MoversScreen.loader'
import type { MoversQuery, MoversView } from '@/schemas/movers'
import { pageSizeSchema } from '@/schemas/table'

/** Known without data, so the pending page draws the real one. */
const HEADER = <TitleRow title="Movers" />

/**
 * The table is this route's MAIN data (see the loader), keyed on the whole
 * search query, so the first load draws one page-wide skeleton rather than a
 * skeleton per card — same reasoning as Market · Overview and Sectors.
 */
function MoversPending() {
  const { view } = Route.useSearch()

  return (
    <ScreenLayout
      pending
      pendingLabel="Loading the movers table…"
      header={HEADER}
      pageTitle="Movers"
    >
      <MoversBoardSkeleton brokers={view === 'brokers'} />
    </ScreenLayout>
  )
}

/** Kept light — RouteErrorPage plus the same header the pending page shows —
 *  so the frame does not change between the two: `errorComponent`s ship in
 *  the entry chunk, and a chunk that cannot load offline reloads the app. */
function MoversError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} />
}

export const Route = createFileRoute('/_app/market/movers')({
  validateSearch: moversSearchSchema,
  loader: loadMovers,
  pendingComponent: MoversPending,
  errorComponent: MoversError,
  component: MoversScreen,
})

/**
 * V2 (`4236:104328`) draws six tabs: the five orderings of the movers table, then Brokers, which
 * reads `/market/brokers/` instead (see `moversService.brokers`).
 */
const VIEW_TABS: TabBarOption[] = [
  { value: 'gainers', label: 'Gainers' },
  { value: 'losers', label: 'Losers' },
  { value: 'turnover', label: 'Turnover' },
  { value: 'volume', label: 'Volume' },
  { value: 'transactions', label: 'Transactions' },
  { value: 'brokers', label: 'Brokers' },
]

const CAPTIONS: Record<MoversView, string> = {
  gainers: "Today's biggest gainers",
  losers: "Today's biggest losers",
  turnover: 'Names ranked by turnover today',
  volume: 'Names ranked by volume today',
  transactions: 'Names ranked by number of transactions today',
}

type Search = ReturnType<typeof toMoversQuery>
type Navigate = ReturnType<typeof Route.useNavigate>

/**
 * The sector chip, on its own so the sector list is only asked for on a tab it narrows — the
 * Brokers tab has no sector to filter by.
 */
function SectorFilter({ sector, navigate }: { sector: string | undefined; navigate: Navigate }) {
  const sectors = useDepthSectors()

  return (
    <MoversSectorFilter
      sectors={sectors.data ?? []}
      sector={sector}
      onChange={(next) => {
        void navigate({ search: (prev) => ({ ...prev, sector: next, page: 1 }) })
      }}
    />
  )
}

/** The pager, shared by both feeds: it only ever writes the page and the page size. */
function MoversPager({
  search,
  total,
  unit,
  navigate,
}: {
  search: Search
  total: number
  unit: string
  navigate: Navigate
}) {
  return (
    <Pagination
      page={search.page}
      pageSize={search.pageSize}
      total={total}
      unit={unit}
      onPageChange={(page) => {
        void navigate({ search: (prev) => ({ ...prev, page }) })
      }}
      onPageSizeChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, pageSize: pageSizeSchema.parse(next), page: 1 }),
        })
      }}
    />
  )
}

/** One of the five orderings of the movers table. */
function MoversTableBoard({
  query,
  search,
  navigate,
}: {
  query: MoversQuery
  search: Search
  navigate: Navigate
}) {
  const table = useMovers(query)

  return (
    <>
      <MoversTable
        view={query.view}
        rows={table.data?.rows ?? []}
        isLoading={table.isLoading}
        isError={table.isError}
        onRetry={() => {
          void table.refetch()
        }}
        retrying={table.isFetching}
        pageSize={search.pageSize}
        caption={CAPTIONS[query.view]}
      />
      <MoversPager
        search={search}
        total={table.data?.total ?? 0}
        unit="names"
        navigate={navigate}
      />
    </>
  )
}

/** The Brokers tab: the same table and pager over the brokers feed. */
function MoversBrokersBoard({ search, navigate }: { search: Search; navigate: Navigate }) {
  const brokers = useMoversBrokers(toBrokersQuery(search))

  return (
    <>
      <MoversBrokersTable
        rows={brokers.data?.rows ?? []}
        isLoading={brokers.isLoading}
        isError={brokers.isError}
        onRetry={() => {
          void brokers.refetch()
        }}
        retrying={brokers.isFetching}
        pageSize={search.pageSize}
      />
      <MoversPager
        search={search}
        total={brokers.data?.total ?? 0}
        unit="brokers"
        navigate={navigate}
      />
    </>
  )
}

function MoversScreen() {
  // Re-parsed through the loader's own schema rather than read straight off
  // `Route.useSearch()`: the router merges the URL's raw query string into
  // that object, so an unrecognised param (`?utm_source=...`) would otherwise
  // key `useMovers` here off a different object than the one the loader
  // warmed for the entry URL — see `MoversScreen.loader.ts`'s own comment on
  // `toMoversQuery`.
  const search = toMoversQuery(Route.useSearch())
  const tableQuery = toTableQuery(search)
  const navigate = Route.useNavigate()

  return (
    <ScreenLayout header={HEADER} pageTitle="Movers">
      {/* The tabs and the table are one block, 16px apart: the pages revamp's one gap from a
          toolbar to its first block, and not the column's 24 between blocks. The tabs and the
          sector chip are one row in V2 (`4236:104328`), which is why they are one child here
          rather than two siblings.

          `@2xl`, not `lg:` — this sits in ScreenLayout's main-column
          `@container`, and the six tabs plus the trigger need roughly 740px
          of that track, which a window breakpoint cannot see behind the
          sidebar and the rail. Under it the two stack — `@sm:items-start` so
          the stretch a column gives its items stops overriding the trigger's
          own width.

          `flex-wrap` is the release valve. Flex breaks a line on the items'
          own widths before it shrinks any of them, so a track too tight for
          the tabs and the trigger drops the trigger to a second line — still
          right-aligned, by its own `ml-auto` — rather than squeezing the tab
          strip into a stub that scrolls. Nothing here can push the page
          sideways either way. */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 @sm:items-start @2xl:flex-row @2xl:flex-wrap @2xl:items-center">
          {/* Six tabs run wider than a 375px phone as underlined text, so the row scrolls inside
              its own track rather than pushing the whole page sideways. It bleeds through the
              shell gutter so the clipped tab at the edge is the affordance — wrapping was the
              alternative, and a tab row that has moved to a second line no longer reads as a row
              of sibling views. The tabs measure ~580px, so the strip stops scrolling at `@2xl`
              (672px of main column), not at a window width: a 1280 window with the Mitra panel
              open leaves the column ~510px whatever the viewport says. */}
          <div className="scrollbar-slim -mx-shell-x min-w-0 overflow-x-auto overscroll-x-contain @2xl:mx-0 @2xl:overflow-x-visible">
            <TabBar
              stripClassName="px-shell-x @2xl:px-0"
              options={VIEW_TABS}
              value={search.view}
              ariaLabel="Movers ordering"
              onChange={(value) => {
                // Back to page 1: a new ordering means page 4 is a different four
                // hundred names, and landing there would read as a broken table.
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    view: value as MoversScreenView,
                    // No head sorts any more, so a tab's own order is the only one: an old link's
                    // `?sortBy=` is dropped on the first tab click.
                    sortBy: undefined,
                    sortDesc: undefined,
                    page: 1,
                  }),
                })
              }}
            />
          </div>

          {tableQuery === null ? null : <SectorFilter sector={search.sector} navigate={navigate} />}
        </div>

        {/* No `gap-*` between the table and its pager: `Pagination` draws its own 24px — a gap
            here would add to it. See its PLACEMENT note. */}
        <div>
          {tableQuery === null ? (
            <MoversBrokersBoard search={search} navigate={navigate} />
          ) : (
            <MoversTableBoard query={tableQuery} search={search} navigate={navigate} />
          )}
        </div>
      </div>
    </ScreenLayout>
  )
}
